import { useState, useEffect, useCallback } from 'react'
import api from '../../../services/api'

const mapFilterLabel = (label) => {
  switch (label) {
    case 'All Time': return 'allTime'
    case 'Today': return 'today'
    case 'This Week': return 'week'
    case 'This Month': return 'month'
    case 'This Year': return 'year'
    case 'Other': return 'other'
    default: return 'allTime'
  }
}

const useAiCostOrgSummary = ({ tableFilter, dateFrom, dateTo, selectedOrg, selectedType } = {}) => {
  const [state, setState] = useState({
    data: null,
    loading: true,
    error: null
  })

  const fetchData = useCallback(async () => {
    if (tableFilter === 'Other' && (!dateFrom || !dateTo)) {
      setState(prev => ({ ...prev, loading: false }))
      return
    }

    setState(prev => ({ ...prev, loading: true, error: null }))

    const date = mapFilterLabel(tableFilter || 'All Time')

    try {
      const response = await api.reports.getCostDashboardSummary({
        date,
        from: dateFrom || '',
        to: dateTo || '',
        ...(selectedOrg ? { org_id: selectedOrg } : {}),
        ...(selectedType ? { type: selectedType } : {})
      })

      const responseData = response?.data || response
      if (responseData?.status === 'error') {
        throw new Error(responseData?.message || 'Failed to fetch summary data')
      }

      const costData = responseData?.cost || responseData || {}
      const colorMap = {
        'ai-image': '#5B6EE2',
        'ai_video': '#C9D3FA',
        audio: '#3875F6',
        course: '#44959A',
        podcast: '#EB3B88',
        maze: '#9FD4FB',
        sharebot: '#2DCCB9',
        image: '#5B6EE2'
      }

      const serviceUsage = Array.isArray(costData.service_usage)
        ? costData.service_usage.map(item => {
          let type = item.type?.toLowerCase() || ''
          if (type === 'image') type = 'ai-image'
          else if (type === 'video' || type === 'ai_video') type = 'ai_video'

          let displayName = type.charAt(0).toUpperCase() + type.slice(1)
          if (type === 'ai-image') displayName = 'AI-Image'
          else if (type === 'ai_video') displayName = 'Ai-Video'

          const creditValue = costData.credit_value || 0
          const rawCredits = item.used_credits !== undefined ? item.used_credits : item.credits
          const credits = typeof rawCredits === 'number' ? rawCredits : parseFloat(rawCredits) || 0
          const cost = item.total_credit_cost !== undefined ? (parseFloat(item.total_credit_cost) || 0) : credits * creditValue

          return {
            type,
            displayName,
            credits,
            percent: item.percent || 0,
            cost,
            color: colorMap[type] || '#888'
          }
        })
        : []

      const totalCredits = costData.total_credits || 0
      const totalUsed = parseFloat(costData.total_used) || 0
      const remainingCredits = costData.remaining_credits || 0
      const consumedPercent = costData.consumed_percent || 0
      const creditValue = costData.credit_value || 0
      const purchasedCredits = costData.purchased_credits || 0

      // Total cost = total used credits × credit value
      const totalCost = totalUsed * creditValue

      // Timeline data extraction
      const timelineData = Array.isArray(costData.timeline_data)
        ? costData.timeline_data.map(item => ({
          date: item.date || item.timestamp,
          credits: typeof item.credits === 'number' ? item.credits : parseFloat(item.credits) || 0,
          cost: (typeof item.credits === 'number' ? item.credits : parseFloat(item.credits) || 0) * creditValue
        }))
        : []

      setState({
        data: {
          totalCredits,
          totalUsed,
          remainingCredits,
          consumedPercent,
          creditValue,
          purchasedCredits,
          totalCost,
          serviceUsage,
          timelineData
        },
        loading: false,
        error: null
      })
    } catch (err) {
      console.error('Error fetching org credit summary:', err)
      setState({
        data: null,
        loading: false,
        error: err.message || 'Failed to fetch summary'
      })
    }
  }, [tableFilter, dateFrom, dateTo, selectedOrg, selectedType])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    ...state,
    refetch: fetchData
  }
}

export default useAiCostOrgSummary
